import type { ResidenceData } from '@repo/sdk/gen'
import {
	GROUP_FIELDS,
	type ResidenceFieldGroup
} from '../../residences/-components/residence-form'

export const TABS = [
	'identity',
	'sites',
	'services',
	'photos',
	'seats',
	'contract',
	'quota70'
] as const
export type StructureTab = (typeof TABS)[number]

/** The tabs that hold no field of the residence: the photos save on their own, the 70% quota is the work after the call. */
const NO_FIELDS = ['photos', 'quota70'] as const

/** The field group whose errors mark each tab. */
export const TAB_GROUP = {
	identity: 'identity',
	sites: 'sites',
	services: 'services',
	seats: 'seats',
	contract: 'contract'
} as const satisfies Record<
	Exclude<StructureTab, (typeof NO_FIELDS)[number]>,
	ResidenceFieldGroup
>

/** Whether a tab holds fields of the residence, and so can show their errors. */
export const hasFields = (tab: StructureTab): tab is keyof typeof TAB_GROUP =>
	!(NO_FIELDS as readonly StructureTab[]).includes(tab)

/** The tab that asks for a field, so a problem in the list can open it. */
export function tabOfField(
	field: keyof ResidenceData
): StructureTab | undefined {
	// Every problem of the sites, the address of the first one included, is on its own tab.
	if (field === 'sites') {
		return 'sites'
	}
	return TABS.find(
		(tab) =>
			hasFields(tab) &&
			(GROUP_FIELDS[TAB_GROUP[tab]] as readonly string[]).includes(field)
	)
}

/**
 * Whether a contract or payment datum is still empty: the call does not name
 * them, so they never block it, but the offers do not start without them.
 * Filled as the API reads it: a number, or text that is not blank.
 */
export function contractMissing(data: ResidenceData) {
	return GROUP_FIELDS.contract.some((field) => {
		const value = data[field]
		return typeof value === 'string' ? value.trim() === '' : value === null
	})
}
